import { IDockviewPanelProps } from "dockview-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type * as PlotlyTypes from "plotly.js";
// Use the pre-built dist bundle (same path react-plotly.js uses) to avoid webpack 5 polyfill issues
// eslint-disable-next-line @typescript-eslint/no-var-requires
const Plotly = require("plotly.js/dist/plotly") as typeof PlotlyTypes;

const WS_URL = "ws://localhost:7879/ws";

// NYC local timestamps come as "YYYY-MM-DDTHH:MM:SS"
const RTH_START = 9 * 60 + 30;
const RTH_END   = 16 * 60;

type SubSession = "rth" | "pm" | "ah";

function getSubSession(t: string): SubSession {
  const timePart = t.split("T")[1] ?? "00:00:00";
  const [h, m] = timePart.split(":").map(Number);
  const mins = h * 60 + m;
  if (mins >= RTH_START && mins < RTH_END) return "rth";
  if (mins < RTH_START) return "pm";
  return "ah";
}

// Keep a simple rth/ext helper for shape computation
function getSession(t: string): "rth" | "ext" {
  return getSubSession(t) === "rth" ? "rth" : "ext";
}

const INC_RTH    = "#17BECF";  // cyan  — positive regular hours (matches IBKR component)
const INC_EXT    = "#F9A825";  // amber — positive pre/after-hours
const DEC_COLOR  = "#7F7F7F";  // gray  — negative regular/after-hours (matches IBKR component)
const DEC_PM     = "#b3b3b3";  // light gray — negative premarket

function barColor(t: string, isUp: boolean): string {
  if (!isUp) return getSubSession(t) === "pm" ? DEC_PM : DEC_COLOR;
  return getSubSession(t) === "rth" ? INC_RTH : INC_EXT;
}

interface Bar {
  t: string;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}

const AH_HI_COLOR   = "#5b9bd5";   // light blue, dashed
const AH_LO_COLOR   = "#ffb3b3";   // light red,  solid
const PM_HI_COLOR   = INC_RTH;     // blue,       dashed
const PM_LO_COLOR   = "#e53935";   // red,        solid
const PREV_CL_COLOR = "#000000";   // black,      solid

interface ShapeResult {
  shapes: Partial<Plotly.Shape>[];
  legendTraces: any[];
}

// ── Compute dashed/solid hline shapes + matching legend traces ────────────────
function computeShapes(bars: Bar[]): ShapeResult {
  const shapes: Partial<Plotly.Shape>[] = [];
  const legendTraces: any[] = [];
  if (!bars.length) return { shapes, legendTraces };

  const lastDay = bars[bars.length - 1].t.split("T")[0];

  let ahHi = -Infinity, ahLo = Infinity, haAh = false;
  let pmHi = -Infinity, pmLo = Infinity, haPm = false;

  for (const b of bars) {
    const day = b.t.split("T")[0];
    const dayDiff = (new Date(lastDay).getTime() - new Date(day).getTime()) / 86400000;
    if (dayDiff > 1) continue;
    if (getSession(b.t) === "ext") {
      const [h] = b.t.split("T")[1].split(":").map(Number);
      if (h >= 16) {
        ahHi = Math.max(ahHi, b.h); ahLo = Math.min(ahLo, b.l); haAh = true;
      } else {
        pmHi = Math.max(pmHi, b.h); pmLo = Math.min(pmLo, b.l); haPm = true;
      }
    }
  }

  // Previous RTH day close
  const rthBars = bars.filter(b => getSession(b.t) === "rth");
  const rthDays = Array.from(new Set(rthBars.map(b => b.t.split("T")[0]))).sort();
  let prevDayClose: number | null = null;
  if (rthDays.length >= 2) {
    const prevDay = rthDays[rthDays.length - 2];
    const last = rthBars.filter(b => b.t.startsWith(prevDay));
    if (last.length) prevDayClose = last[last.length - 1].c;
  }

  const hline = (y: number, color: string, dash: string, width = 1): Partial<Plotly.Shape> => ({
    type: "line" as const, xref: "paper" as const, yref: "y" as const,
    x0: 0, x1: 1, y0: y, y1: y,
    line: { color, dash: dash as any, width },
  });

  const leg = (name: string, color: string, dash: string, width = 1): any => ({
    type: "scatter", x: [null], y: [null], mode: "lines",
    name, showlegend: true,
    line: { color, dash, width },
  });

  if (haAh) {
    shapes.push(hline(ahHi, AH_HI_COLOR, "dash"));
    shapes.push(hline(ahLo, AH_LO_COLOR, "solid"));
    legendTraces.push(leg("AH High", AH_HI_COLOR, "dash"));
    legendTraces.push(leg("AH Low",  AH_LO_COLOR, "solid"));
  }
  if (haPm) {
    shapes.push(hline(pmHi, PM_HI_COLOR, "dash"));
    shapes.push(hline(pmLo, PM_LO_COLOR, "solid"));
    legendTraces.push(leg("PM High", PM_HI_COLOR, "dash"));
    legendTraces.push(leg("PM Low",  PM_LO_COLOR, "solid"));
  }
  if (prevDayClose !== null) {
    shapes.push(hline(prevDayClose, PREV_CL_COLOR, "solid", 1.5));
    legendTraces.push(leg("Prev Close", PREV_CL_COLOR, "solid", 1.5));
  }

  return { shapes, legendTraces };
}

// ── Build the base layout ─────────────────────────────────────────────────────
function makeLayout(shapes: Partial<Plotly.Shape>[] = []): Partial<Plotly.Layout> {
  return {
    paper_bgcolor: "#ffffff",
    plot_bgcolor:  "#ffffff",
    font: { color: "#333333", size: 11 },
    margin: { l: 60, r: 60, t: 35, b: 50 },
    showlegend: true,
    legend: {
      orientation: "h" as const,
      x: 0, y: 1.02,
      xanchor: "left" as const,
      yanchor: "bottom" as const,
      font: { size: 10 },
      bgcolor: "rgba(255,255,255,0.8)",
    },
    xaxis: {
      type: "date" as const,
      color: "#444444",
      gridcolor: "#eeeeee",
      linecolor: "#cccccc",
      tickformat: "%m/%d %H:%M",
      tickangle: -45,
      rangeslider: { visible: false },
    },
    yaxis: {
      domain: [0.28, 1.0] as [number, number],
      color: "#444444",
      gridcolor: "#eeeeee",
      linecolor: "#cccccc",
      autorange: true as const,
    },
    yaxis2: {
      domain: [0, 0.22] as [number, number],
      color: "#444444",
      gridcolor: "#eeeeee",
      linecolor: "#cccccc",
      title: { text: "Vol", font: { size: 9 } },
    },
    shapes: shapes as Plotly.Shape[],
  };
}

// ── Trace indices ─────────────────────────────────────────────────────────────
const T_RTH  = 0;   // historical RTH candlesticks
const T_PM   = 1;   // historical premarket candlesticks
const T_AH   = 2;   // historical after-hours candlesticks
const T_CUR  = 3;   // current (live) bar — single bar
const T_VOL  = 4;   // historical volume
const T_VCUR = 5;   // current bar volume — single bar

export function RealtimeIBKR(_props: IDockviewPanelProps) {
  const plotDiv   = useRef<HTMLDivElement>(null);
  const idRef     = useRef(Math.random().toString(36).slice(2));
  const wsRef     = useRef<WebSocket | null>(null);
  const curBar    = useRef<Bar | null>(null);
  const volColors = useRef<string[]>([]);
  const volX    = useRef<string[]>([]);
  const volY    = useRef<number[]>([]);
  const plotReady = useRef(false);

  const [ticker, setTicker]       = useState("AAPL");
  const [resolution, setResolution] = useState<"s" | "m" | "h">("m");
  const [status, setStatus]       = useState("Disconnected");
  const [loading, setLoading]     = useState(false);

  // ── Plotly helpers ──────────────────────────────────────────────────────────

  const initChart = useCallback((bars: Bar[]) => {
    if (!plotDiv.current || !bars.length) return;

    const hist    = bars.slice(0, -1);
    const livebar = bars[bars.length - 1];

    curBar.current = livebar;

    const rthBars = hist.filter(b => getSubSession(b.t) === "rth");
    const pmBars  = hist.filter(b => getSubSession(b.t) === "pm");
    const ahBars  = hist.filter(b => getSubSession(b.t) === "ah");

    volX.current      = hist.map(b => b.t);
    volY.current      = hist.map(b => b.v);
    volColors.current = hist.map(b => barColor(b.t, b.c >= b.o));

    // `fillcolor` is valid in Plotly but missing from @types/plotly.js — cast to any
    const candleTrace = (bs: Bar[], inc: string, dec: string): any => ({
      type: "candlestick",
      x: bs.map(b => b.t),
      open: bs.map(b => b.o),
      high: bs.map(b => b.h),
      low: bs.map(b => b.l),
      close: bs.map(b => b.c),
      increasing: { fillcolor: inc, line: { color: inc, width: 1 } },
      decreasing: { fillcolor: dec, line: { color: dec, width: 1 } },
      line: { width: 1 },
      whiskerwidth: 0,
    });

    const liveSub = getSubSession(livebar.t);
    const liveInc = liveSub === "rth" ? INC_RTH : INC_EXT;
    const liveDec = liveSub === "pm" ? DEC_PM : DEC_COLOR;

    const traces: any[] = [
      candleTrace(rthBars, INC_RTH, DEC_COLOR),  // T_RTH
      candleTrace(pmBars,  INC_EXT, DEC_PM),      // T_PM
      candleTrace(ahBars,  INC_EXT, DEC_COLOR),   // T_AH
      {
        type: "candlestick",
        x: [livebar.t],
        open: [livebar.o],
        high: [livebar.h],
        low: [livebar.l],
        close: [livebar.c],
        increasing: { fillcolor: liveInc, line: { color: liveInc, width: 1 } },
        decreasing: { fillcolor: liveDec, line: { color: liveDec, width: 1 } },
        line: { width: 1 },
        whiskerwidth: 0,
      },
      {
        type: "bar" as const,
        x: volX.current.slice(),
        y: volY.current.slice(),
        marker: { color: volColors.current.slice() },
        yaxis: "y2",
      },
      {
        type: "bar" as const,
        x: [livebar.t],
        y: [livebar.v],
        marker: { color: barColor(livebar.t, livebar.c >= livebar.o) },
        yaxis: "y2",
      },
    ];

    const { shapes, legendTraces } = computeShapes(bars);
    const layout = { ...makeLayout(shapes), uirevision: idRef.current };
    Plotly.react(plotDiv.current, [...traces, ...legendTraces] as Plotly.Data[], layout, {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ["lasso2d", "select2d"],
    });
    plotReady.current = true;
  }, []);

  // Called when a new bar completes (is_new=true) or current bar updates (is_new=false)
  const handleBarUpdate = useCallback((bar: Bar, isNew: boolean) => {
    if (!plotDiv.current || !plotReady.current) return;

    if (isNew) {
      // The PREVIOUS current bar just completed — add it to the right historical trace
      const prev = curBar.current;
      if (prev) {
        const sub = getSubSession(prev.t);
        const traceIdx = sub === "rth" ? T_RTH : sub === "pm" ? T_PM : T_AH;
        (Plotly as any).extendTraces(plotDiv.current, {
          x:     [[prev.t]],
          open:  [[prev.o]],
          high:  [[prev.h]],
          low:   [[prev.l]],
          close: [[prev.c]],
        }, [traceIdx]);

        // Extend historical volume (rebuild color array via restyle)
        volX.current.push(prev.t);
        volY.current.push(prev.v);
        volColors.current.push(barColor(prev.t, prev.c >= prev.o));
        (Plotly as any).restyle(plotDiv.current, {
          x:              [volX.current.slice()],
          y:              [volY.current.slice()],
          "marker.color": [volColors.current.slice()],
        }, [T_VOL]);
      }

      // Update current bar to the new live bar
      curBar.current = bar;
      const newSub  = getSubSession(bar.t);
      const liveInc = newSub === "rth" ? INC_RTH : INC_EXT;
      const liveDec = newSub === "pm"  ? DEC_PM  : DEC_COLOR;
      (Plotly as any).restyle(plotDiv.current, {
        x:     [[bar.t]],
        open:  [[bar.o]],
        high:  [[bar.h]],
        low:   [[bar.l]],
        close: [[bar.c]],
        "increasing.fillcolor":    liveInc,
        "increasing.line.color":   liveInc,
        "decreasing.fillcolor":    liveDec,
        "decreasing.line.color":   liveDec,
      }, [T_CUR]);

      (Plotly as any).restyle(plotDiv.current, {
        x: [[bar.t]],
        y: [[bar.v]],
        "marker.color": [barColor(bar.t, bar.c >= bar.o)],
      }, [T_VCUR]);

    } else {
      // In-progress bar update — update OHLCV of current bar
      curBar.current = bar;
      (Plotly as any).restyle(plotDiv.current, {
        close: [[bar.c]],
        high:  [[bar.h]],
        low:   [[bar.l]],
      }, [T_CUR]);
      (Plotly as any).restyle(plotDiv.current, { y: [[bar.v]] }, [T_VCUR]);
    }
  }, []);

  // Called at 0.25 s — updates only close/high/low of the live bar
  const handlePriceTick = useCallback((price: number) => {
    if (!plotDiv.current || !plotReady.current || !curBar.current) return;
    const b = curBar.current;
    const newHigh = Math.max(b.h, price);
    const newLow  = Math.min(b.l, price);
    curBar.current = { ...b, c: price, h: newHigh, l: newLow };
    (Plotly as any).restyle(plotDiv.current, {
      close: [[price]],
      high:  [[newHigh]],
      low:   [[newLow]],
    }, [T_CUR]);
  }, []);

  // ── WebSocket ───────────────────────────────────────────────────────────────

  const connectWS = useCallback(() => {
    if (wsRef.current) wsRef.current.close();
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen  = () => setStatus("Connected");
    ws.onclose = () => { setStatus("Disconnected"); setLoading(false); };
    ws.onerror = () => { setStatus("Error"); setLoading(false); };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.function === "bars") {
          initChart(msg.bars as Bar[]);
          setLoading(false);
        } else if (msg.function === "bar_update") {
          handleBarUpdate(msg.bar as Bar, msg.is_new as boolean);
        } else if (msg.function === "price") {
          handlePriceTick(msg.price as number);
        } else if (msg.error) {
          setStatus(`Error: ${msg.error}`);
          setLoading(false);
        }
      } catch {
        // ignore parse errors
      }
    };
  }, [initChart, handleBarUpdate, handlePriceTick]);

  useEffect(() => {
    connectWS();
    return () => { wsRef.current?.close(); };
  }, [connectWS]);

  const subscribe = (e: React.FormEvent) => {
    e.preventDefault();
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    plotReady.current = false;
    curBar.current = null;
    volX.current = [];
    volY.current = [];
    volColors.current = [];
    setLoading(true);
    ws.send(JSON.stringify({ function: "subscribe", ticker, resolution }));
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#ffffff", color: "#333" }}>
      {/* Controls */}
      <form
        onSubmit={subscribe}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderBottom: "1px solid #e0e0e0" }}
      >
        <input
          value={ticker}
          onChange={e => setTicker(e.target.value.toUpperCase())}
          placeholder="Ticker"
          style={{
            width: 80, padding: "3px 6px", background: "#ffffff", color: "#333",
            border: "1px solid #cccccc", borderRadius: 4, fontFamily: "monospace",
          }}
        />
        <select
          value={resolution}
          onChange={e => setResolution(e.target.value as "s" | "m" | "h")}
          style={{
            padding: "3px 6px", background: "#ffffff", color: "#333",
            border: "1px solid #cccccc", borderRadius: 4,
          }}
        >
          <option value="s">5 s</option>
          <option value="m">1 min</option>
          <option value="h">1 hr</option>
        </select>
        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "3px 12px", background: "#1565C0", color: "white",
            border: "none", borderRadius: 4, cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? "Loading…" : "Load"}
        </button>
        <span style={{ fontSize: 11, color: status.startsWith("Error") ? "#c62828" : "#888" }}>
          {status}
        </span>
      </form>

      {/* Chart */}
      <div ref={plotDiv} style={{ flex: 1, minHeight: 0 }} />
    </div>
  );
}
